import React, { useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Space } from "antd";
import { useString as s } from "../../../../components/StringProvider";
import { useAgentApiKey } from "../../../../providers/agentApiKeyProvider";
import { selectIsCuvamaUser } from "../../../../store/reducers";
import { useFeature } from "../../../../components/FeaturesProvider";
import Features from "../../../../utils/constants/Features";
import NewButton from "../../../../components/NewButton";
import PasswordInput from "../../../../components/Input/PasswordInput";
import Heading from "../../../../components/Heading";
import Text from "../../../../components/Text";
import ButtonGroup from "../../../../components/ButtonGroup";
import { Modal } from "../../../../components";
import Notification from "../../../../components/Notification";

const AgentApiKeySection = () => {
  const isCuvamaUser = useSelector(selectIsCuvamaUser);
  const cuvamaDiscoveryEnabled = useFeature(
    Features.CUVAMA_DISCOVERY_INTEGRATION,
    false
  );

  const {
    agentApiKey,
    setupLoading,
    regenerateLoading,
    setupAgent,
    regenerateAgentApiKey
  } = useAgentApiKey();

  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);

  const sectionTitle = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.title",
    "Discovery to Value Case API Key"
  );
  const helperText = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.helperText",
    "Use this API key to authorise calls from the Discovery app into the Value Case app for this tenant."
  );
  const setupButtonLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.setupButton",
    "Set up Agent"
  );
  const copyButtonLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.copyButton",
    "Copy"
  );
  const regenerateButtonLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.regenerateButton",
    "Regenerate API Key"
  );
  const agentEmailLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.agentEmailLabel",
    "Agent user"
  );
  const apiKeyLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.apiKeyLabel",
    "API Key"
  );
  const setupErrorMessage = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.messages.setupError",
    "Failed to set up the Cuvama agent. Please try again."
  );
  const setupSuccessMessage = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.messages.setupSuccess",
    "Cuvama agent set up successfully."
  );
  const regenerateSuccessMessage = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.messages.regenerateSuccess",
    "API key regenerated successfully."
  );
  const regenerateErrorMessage = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.messages.regenerateError",
    "Failed to regenerate the API key. Please try again."
  );
  const copySuccessMessage = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.messages.copySuccess",
    "API key copied to clipboard."
  );
  const copyErrorMessage = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.messages.copyError",
    "Failed to copy API key to clipboard."
  );
  const regenerateModalTitle = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.regenerateModal.title",
    "Regenerate API Key"
  );
  const regenerateModalSubheader = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.regenerateModal.subheader",
    "This will invalidate the existing API key. Any system using the current key will stop working until it is updated. Are you sure you want to continue?"
  );
  const regenerateModalConfirmLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.regenerateModal.confirmLabel",
    "Regenerate"
  );
  const regenerateModalCancelLabel = s(
    "generalAdmin.page.cuvamaDiscoveryAgent.regenerateModal.cancelLabel",
    "Cancel"
  );

  if (!isCuvamaUser || !cuvamaDiscoveryEnabled) {
    return null;
  }

  const hasAgentApiKey = Boolean(agentApiKey?.apiKey);

  const onSetupAgent = async () => {
    try {
      await setupAgent();
      Notification.success(setupSuccessMessage);
    } catch (err) {
      Notification.error(setupErrorMessage);
    }
  };

  const onCopy = async () => {
    if (!agentApiKey?.apiKey) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(agentApiKey.apiKey);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = agentApiKey.apiKey;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      Notification.success(copySuccessMessage);
    } catch (err) {
      Notification.error(copyErrorMessage);
    }
  };

  const onRegenerateClick = () => setRegenerateModalOpen(true);
  const onRegenerateCancel = () => setRegenerateModalOpen(false);

  const onRegenerateConfirm = async () => {
    try {
      await regenerateAgentApiKey();
      Notification.success(regenerateSuccessMessage);
      setRegenerateModalOpen(false);
    } catch (err) {
      Notification.error(regenerateErrorMessage);
    }
  };

  return (
    <SectionContainer data-cy="agent-api-key-section">
      <Heading level="h4">{sectionTitle}</Heading>
      <Text>{helperText}</Text>

      {!hasAgentApiKey && (
        <NewButton
          type="primary"
          loading={setupLoading}
          onClick={onSetupAgent}
          data-cy="agent-api-key-setup-button"
        >
          {setupButtonLabel}
        </NewButton>
      )}

      {hasAgentApiKey && (
        <KeyDetails>
          {agentApiKey?.agentEmail && (
            <DetailRow>
              <DetailLabel>{agentEmailLabel}</DetailLabel>
              <Text data-cy="agent-api-key-email">
                {agentApiKey.agentEmail}
              </Text>
            </DetailRow>
          )}

          <DetailRow>
            <DetailLabel>{apiKeyLabel}</DetailLabel>
            <PasswordInput
              data-cy="agent-api-key-input"
              value={agentApiKey?.apiKey ?? ""}
              readOnly
              block
            />
          </DetailRow>

          <Space>
            <NewButton
              type="secondary"
              onClick={onCopy}
              data-cy="agent-api-key-copy-button"
            >
              {copyButtonLabel}
            </NewButton>
            <NewButton
              type="secondary"
              onClick={onRegenerateClick}
              loading={regenerateLoading}
              data-cy="agent-api-key-regenerate-button"
            >
              {regenerateButtonLabel}
            </NewButton>
          </Space>
        </KeyDetails>
      )}

      {regenerateModalOpen && (
        <Modal
          title={regenerateModalTitle}
          open={true}
          onCancel={onRegenerateCancel}
          data-cy="agent-api-key-regenerate-modal"
        >
          <ModalText>
            <Text>{regenerateModalSubheader}</Text>
          </ModalText>
          <ButtonGroup>
            <NewButton
              type="primary"
              onClick={onRegenerateConfirm}
              loading={regenerateLoading}
              data-cy="agent-api-key-regenerate-confirm-button"
            >
              {regenerateModalConfirmLabel}
            </NewButton>
            <NewButton
              type="secondary"
              onClick={onRegenerateCancel}
              data-cy="agent-api-key-regenerate-cancel-button"
            >
              {regenerateModalCancelLabel}
            </NewButton>
          </ButtonGroup>
        </Modal>
      )}
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #e8e8e8;
`;

const KeyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
`;

const ModalText = styled.div`
  margin-bottom: 16px;
`;

export default AgentApiKeySection;
